import { Component } from '@theme/component';

/**
 * A small utility for setting up an animated disclosure panel using inert and CSS display grid for height auto animation.
 *
 * This component works with any element that follows the details/summary pattern for revealing content.
 * Forms and other interactive elements don't need to live inside a details element, but can still use
 * the expanded pattern for accessibility and animation.
 *
 * The component relies on CSS display grid and inert attributes to create smooth height animations
 * when expanding/collapsing content areas.
 *
 * @class DisclosureCustom
 * @typedef {object} Refs
 * @property {HTMLElement} disclosureTrigger – The disclosure trigger element.
 * @property {HTMLElement} disclosureContent – The disclosure content element.
 *
 * @extends Component<Refs>
 */
class DisclosureCustom extends Component {
  requiredRefs = ['disclosureTrigger', 'disclosureContent'];

  /** @type {number | null} */
  #lastToggleTime = null;

  #usesPointerEvents = false;

  connectedCallback() {
    super.connectedCallback();
    this.#usesPointerEvents = 'PointerEvent' in window;

    if (this.#usesPointerEvents) {
      this.refs.disclosureTrigger.addEventListener('pointerup', this.toggleDisclosure);
    } else {
      this.refs.disclosureTrigger.addEventListener('touchend', this.toggleDisclosure, { passive: false });
    }

    this.refs.disclosureTrigger.addEventListener('click', this.toggleDisclosure);
    this.#setContentExpanded(this.refs.disclosureTrigger.matches('[aria-expanded="true"]'));
  }

  disconnectedCallback() {
    if (this.#usesPointerEvents) {
      this.refs.disclosureTrigger?.removeEventListener('pointerup', this.toggleDisclosure);
    } else {
      this.refs.disclosureTrigger?.removeEventListener('touchend', this.toggleDisclosure);
    }

    this.refs.disclosureTrigger?.removeEventListener('click', this.toggleDisclosure);
    super.disconnectedCallback();
  }

  /**
   * Toggles the disclosure panel open/closed state.
   * Updates aria-expanded, aria-label attributes on the trigger and inert state on content.
   * Works with CSS grid animations to provide smooth height transitions.
   */
  toggleDisclosure = (event) => {
    const isPointerEvent = window.PointerEvent && event instanceof window.PointerEvent;

    if (isPointerEvent && (event.button !== 0 || !event.isPrimary)) return;
    if (event instanceof MouseEvent && !isPointerEvent && event.button !== 0) return;

    event?.preventDefault();
    event?.stopPropagation();
    event?.stopImmediatePropagation();

    const now = performance.now();
    if (this.#lastToggleTime && now - this.#lastToggleTime < 700) return;
    this.#lastToggleTime = now;

    const { disclosureTrigger: trigger } = this.refs;
    const expanded = trigger.matches('[aria-expanded="true"]');
    const nextExpanded = !expanded;

    trigger.setAttribute('aria-expanded', String(nextExpanded));
    trigger.setAttribute(
      'aria-label',
      `${expanded ? trigger.dataset.disclosureOpen : trigger.dataset.disclosureClose}`
    );
    this.#setContentExpanded(nextExpanded);
  };

  /**
   * @param {boolean} expanded
   */
  #setContentExpanded(expanded) {
    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs;

    this.toggleAttribute('data-expanded', expanded);
    trigger.setAttribute('data-expanded', String(expanded));
    content.setAttribute('data-expanded', String(expanded));
    content.toggleAttribute('inert', !expanded);
    content.inert = !expanded;
  }
}

if (!customElements.get('disclosure-custom')) {
  customElements.define('disclosure-custom', DisclosureCustom);
}
